import { ref, computed, watch } from 'vue'

/**
 * Reusable pagination composable.
 *
 * Replaces the 8+ duplicated pagination patterns across admin/agent views.
 *
 * @param {import('vue').ComputedRef<Array>} filteredItems - Reactive list to paginate
 * @param {object} [options]
 * @param {number}   [options.defaultPageSize=25]
 * @param {number[]} [options.pageSizeOptions]   - Dropdown choices (optional)
 * @returns {{
 *   currentPage: import('vue').Ref<number>,
 *   pageSize:    import('vue').Ref<number>,
 *   totalPages:  import('vue').ComputedRef<number>,
 *   paginatedItems: import('vue').ComputedRef<Array>,
 *   paginationInfo: import('vue').ComputedRef<{start:number,end:number,total:number}>,
 *   visiblePages: import('vue').ComputedRef<Array<number|string>>,
 *   pageSizeOptions: number[],
 *   resetPagination: () => void,
 *   changePageSize:  (size: number) => void,
 *   prevPage: () => void,
 *   nextPage: () => void,
 * }}
 */
export function usePagination(filteredItems, options = {}) {
  const { defaultPageSize = 25, pageSizeOptions = [10, 25, 50, 100] } = options

  const currentPage = ref(1)
  const pageSize = ref(defaultPageSize)

  const totalPages = computed(() => {
    return Math.ceil(filteredItems.value.length / pageSize.value) || 1
  })

  const paginatedItems = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value
    return filteredItems.value.slice(start, start + pageSize.value)
  })

  const paginationInfo = computed(() => {
    const total = filteredItems.value.length
    if (total === 0) return { start: 0, end: 0, total: 0 }
    const start = (currentPage.value - 1) * pageSize.value + 1
    const end = Math.min(currentPage.value * pageSize.value, total)
    return { start, end, total }
  })

  // Ellipsis-style page list for pagination UI
  const visiblePages = computed(() => {
    const total = totalPages.value
    const current = currentPage.value
    const pages = []

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      if (current > 3) pages.push('...')
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i)
      }
      if (current < total - 2) pages.push('...')
      pages.push(total)
    }

    return pages
  })

  // Auto-clamp: when the list shrinks and the current page is beyond the last page
  watch(totalPages, (tp) => {
    if (currentPage.value > tp) {
      currentPage.value = tp
    }
  })

  function resetPagination() {
    currentPage.value = 1
  }

  function changePageSize(newSize) {
    pageSize.value = newSize
    currentPage.value = 1
  }

  function prevPage() {
    if (currentPage.value > 1) currentPage.value--
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++
  }

  return {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems,
    paginationInfo,
    visiblePages,
    pageSizeOptions,
    resetPagination,
    changePageSize,
    prevPage,
    nextPage,
  }
}
